import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import {
  ACCENT_STORAGE_KEY,
  applyAccent,
  readStoredAccent,
  type Accent,
} from '@/lib/accents';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  accent: Accent;
  setAccent: (accent: Accent) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'theme';

// Apply the saved accent before first render so pages don't flash the default.
applyAccent(readStoredAccent());

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  });
  const [accent, setAccentState] = useState<Accent>(readStoredAccent);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    applyAccent(accent);
    try {
      localStorage.setItem(ACCENT_STORAGE_KEY, accent);
    } catch {
      // ignore
    }
  }, [accent]);

  const setTheme = (nextTheme: Theme) => setThemeState(nextTheme);
  const setAccent = (nextAccent: Accent) => setAccentState(nextAccent);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, accent, setAccent }),
    [theme, accent],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function UseTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('UseTheme must be used within ThemeProvider');
  }
  return context;
}

export function useTheme() {
  return UseTheme();
}
